<template>
  <div>
    <div
      id="bee-plugin-container"
      class="-mx-6 -mt-2"
      style="height: calc(100vh - 68px)"
    ></div>

    <gd-modal
      ref="selectImageModal"
      name="image-picker"
      width="100%"
      height="100%"
      :hideHeader="true"
    >
      <image-picker-without-modal
        :value="''"
        modal="true"
        remove="false"
        @close-modal="closeImageSelector"
        @selected="selectImage"
      />
    </gd-modal>
  </div>
</template>

<script>
import Auth from '@groovepages/gd-lib/auth';
import BeePlugin from '@mailupinc/bee-plugin';
import axios from 'axios';
import directives from '../directives';
import mixins from '../mixins';
import ImagePickerWithoutModal from './ImagePicker';
import GdModal from './Modal';

export default {
  name: 'EmailBuilder',
  directives,
  mixins: [mixins],

  components: {
    GdModal,
    ImagePickerWithoutModal,
  },

  props: {
    templateId: {
      default: 'Zoo8z2OKb9r8',
    },
    templateType: {
      default: 'template',
    },
    builderVariables: {
      default: function () {
        return [];
      },
    },
    allowSaveRows: {
      required: false,
      default: false,
    },
  },

  data() {
    return {
      imageSelecterResolve: null,
      imageSelecterReject: null,
      beeInstance: null,
      beeConfig: {
        container: 'bee-plugin-container',
        saveRows: this.allowSaveRows,
        rowsConfiguration: {
          emptyRows: true,
          defaultRows: true,
          externalContentURLs: [
            {
              name: 'Saved Rows',
              value: 'category-value',
              handle: 'category-handle',
              isLocal: true,
              behaviors: {
                canEdit: false,
                canDelete: true,
              },
            },
          ],
        },
        hooks: {
          getRows: {
            handler: this.manageFetchedRows,
          },
        },
        contentDialog: {
          specialLinks: {
            label: 'Affiliate links',
            handler: this.manageAffiliateLink,
          },
          filePicker: {
            handler: this.imageUploaderHandler,
          },
          saveRow: {
            handler: this.manageSavedRow,
          },
          onDeleteRow: {
            handler: this.manageDeleteRow,
          },
        },
        trackChanges: true,
        loadingSpinnerTheme: 'light',
        onSave: (jsonFile, htmlFile) => {
          this.$emit('hideLoader');
          const jsonObj = JSON.parse(jsonFile);
          if (jsonObj.page.rows[0].columns[0].modules.length) {
            this.$emit('onSaveEmail', jsonFile, htmlFile);
          } else {
            this.$emit('noContentFound');
          }
        },
        onSaveAsTemplate: (jsonFile) => {
          this.$emit('hideLoader');
          const jsonObj = JSON.parse(jsonFile);
          if (jsonObj.page.rows[0].columns[0].modules.length) {
            this.$emit('onSaveTemplate', jsonFile);
          } else {
            this.$emit('noContentFound');
          }
        },
        onChange: (jsonFile, response) => {
          this.$emit('changeInContent');
        },
        onLoad: () => {
          this.$emit('hideLoader');
        },
        onSaveRow: (rowJSON, rowHTML, pageJSON) => {
          const rowJsonObj = JSON.parse(rowJSON);
          this.$emit('onSaveRow', {
            rowName: rowJsonObj.metadata.name,
            rowJson: rowJSON,
          });
        },
      },
    };
  },

  async created() {
    this.addCustomStylesToBuilder();
    this.assignUidToBuilder();
    await this.intializeBeeEditor();
  },

  methods: {
    addCustomStylesToBuilder() {
      if (!window.config.grooveappsurl.includes('localhost')) {
        this.beeConfig.customCss = `${window.config.grooveappsurl}/groovemail/builder/app.css`;
      } else {
        this.beeConfig.customCss = `http://localhost:8080/builder/app.css`;
      }
    },

    assignUidToBuilder() {
      const { id } = Auth.authenticatedUser();
      this.beeConfig.uid = id ? id : null;
    },

    async intializeBeeEditor() {
      const clientId = 'b973f0b5-8ae4-40d5-bf59-d25e6fd497a5';
      const clientSecret =
        'IXe95WkvN9upuxKNKbGEgnCdTpEbrKxIrjdzBi1iApsY3p70NLVJ';
      let BEE_TEMPLATE_URL = '';
      const BEEJS_URL = 'https://app-rsrc.getbee.io/plugin/BeePlugin.js';
      const API_AUTH_URL = 'https://auth.getbee.io/apiauth';
      const conf = { authUrl: API_AUTH_URL, beePluginUrl: BEEJS_URL };
      this.beeInstance = new BeePlugin();
      if (this.builderVariables && this.builderVariables.length) {
        this.beeConfig.mergeTags = this.builderVariables;
      }
      if (this.templateType === 'template') {
        const globalTemplateData = await this.fetchContentData(
          `${window.gdEnv.VUE_APP_API_URL}/groovemail/fetch-template-json/${this.templateId}`
        );
        const globalTemplateJson = globalTemplateData?.data
          ? globalTemplateData.data
          : {};
        await this.fetchTokenAndStartEditor(
          clientId,
          clientSecret,
          conf,
          globalTemplateJson
        );
      } else {
        if (this.templateType === 'user-template') {
          const templateData = await this.fetchContentData(
            `${window.gdEnv.VUE_APP_API_URL}/groovemail/user/template/${this.templateId}`
          );
          const templateJson = templateData?.data?.emailContentSections
            ? JSON.parse(templateData.data.emailContentSections)
            : '';
          await this.fetchTokenAndStartEditor(
            clientId,
            clientSecret,
            conf,
            templateJson
          );
        } else if (this.templateType === 'user-email') {
          const emailData = await this.fetchContentData(
            `${window.gdEnv.VUE_APP_API_URL}/groovemail/email/template/${this.templateId}`
          );
          const emailJson = emailData?.data?.emailContentSections
            ? JSON.parse(emailData.data.emailContentSections)
            : '';
          await this.fetchTokenAndStartEditor(
            clientId,
            clientSecret,
            conf,
            emailJson
          );
        }
      }
    },

    async fetchTokenAndStartEditor(clientId, clientSecret, conf, json) {
      await this.beeInstance.getToken(clientId, clientSecret, conf);
      if (!json.page) {
        const blankJson = this.getBlankJson();
        this.beeInstance.start(this.beeConfig, blankJson);
      } else {
        this.beeInstance.start(this.beeConfig, json);
      }
    },

    getBlankJson() {
      return {
        page: {
          body: {
            container: {
              style: {
                'background-color': '#FFFFFF',
              },
            },
            content: {
              computedStyle: {
                linkColor: '#0068A5',
                messageBackgroundColor: '#F2F2F2',
                messageWidth: '640px',
              },
              style: {
                color: '#000000',
                'font-family':
                  'Montserrat, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma, sans-serif',
              },
            },
            type: 'mailup-bee-page-proprerties',
            webFonts: [
              {
                fontFamily: "'Lato', Tahoma, Verdana, Segoe, sans-serif",
                name: 'Lato',
                url: 'https://fonts.googleapis.com/css?family=Lato',
              },
              {
                fontFamily:
                  "'Montserrat', 'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', Tahoma, sans-serif",
                name: 'Montserrat',
                url: 'https://fonts.googleapis.com/css?family=Montserrat',
              },
            ],
          },
          description: '',
          rows: [
            {
              type: 'one-column-empty',
              container: {
                style: {
                  'background-color': 'transparent',
                  'background-image': 'none',
                  'background-repeat': 'no-repeat',
                  'background-position': 'top left',
                },
              },
              content: {
                style: {
                  'background-color': 'transparent',
                  color: '#000000',
                  width: '500px',
                  'background-image': 'none',
                  'background-repeat': 'no-repeat',
                  'background-position': 'top left',
                },
              },
              columns: [
                {
                  'grid-columns': 12,
                  modules: [],
                  style: {
                    'background-color': 'transparent',
                    'padding-top': '5px',
                    'padding-right': '0px',
                    'padding-bottom': '5px',
                    'padding-left': '0px',
                    'border-top': '0px solid transparent',
                    'border-right': '0px solid transparent',
                    'border-bottom': '0px solid transparent',
                    'border-left': '0px solid transparent',
                  },
                  uuid: 'f893e1dd-1780-4f29-b7bf-c84dc34211fe',
                },
              ],
              uuid: '50aad720-8a93-43f9-9328-88ed3b6112fc',
            },
          ],
          template: {
            name: 'template-base',
            type: 'basic',
            version: '0.0.1',
          },
          title: '',
        },
        comments: {},
      };
    },

    async fetchContentData(url) {
      try {
        this.$emit('showLoader');
        const { token } = Auth.authenticatedUser();
        const currentTemplate = await axios.get(url, {
          headers: {
            Authorization: 'Bearer ' + token, //the token is a variable which holds the token
          },
        });
        return currentTemplate;
      } catch (e) {
        this.$emit('hideLoader');
        return null;
      }
    },

    imageUploaderHandler(resolve, reject, args) {
      this.imageSelecterResolve = resolve;
      this.imageSelecterReject = reject;
      this.$refs.selectImageModal.open();
    },

    selectImage(value) {
      this.imageSelecterResolve({
        url: value.url ? value.url : value,
      });
    },

    closeImageSelector() {
      this.$refs.selectImageModal.close();
      this.imageSelecterReject();
    },

    saveEmail() {
      this.$emit('showLoader');
      this.beeInstance.save();
    },

    saveTemplate() {
      this.$emit('showLoader');
      this.beeInstance.saveAsTemplate();
    },

    manageAffiliateLink(resolve, reject, args) {
      this.affLinkResolve = resolve;
      this.affLinkReject = reject;
      this.$emit('prepareAffiliateLink');
    },

    insertAffiliateLink(affData) {
      if (affData && affData.type && affData.name) {
        this.affLinkResolve({
          type: affData.type,
          label: affData.landing_page_url || affData.link,
          link: affData.landing_page_url || affData.link,
        });
      }
    },

    closeAffiliateSelector() {
      this.affLinkReject();
    },

    manageSavedRow(resolve, reject, args) {
      this.saveRowResolve = resolve;
      this.saveRowReject = reject;
      const rowData = {};
      rowData.id = args?.metadata?.id ? args.metadata.id : null;
      rowData.name =
        args?.metadata?.id && args.metadata.name ? args.metadata.name : '';
      this.$emit('prepareSavingRows', rowData);
    },

    closeRowCreation() {
      this.saveRowReject();
    },

    createNewRow(rowMeta) {
      this.saveRowResolve({
        name: rowMeta.name,
        category: 'New',
      });
    },

    async manageFetchedRows(resolve, reject, args) {
      let rows = [];
      if (this.allowSaveRows) {
        this.$emit('showLoader');
        try {
          const { token } = Auth.authenticatedUser();
          rows = await axios.get(
            `${window.gdEnv.VUE_APP_API_URL}/groovemail/user/row/list`,
            {
              headers: {
                Authorization: 'Bearer ' + token, //the token is a variable which holds the token
              },
            }
          );
          this.$emit('hideLoader');
        } catch (err) {
          this.$emit('hideLoader');
        }
      }
      const rowsModified = [];
      if (rows && rows.data && rows.data.data && rows.data.data.length) {
        rows.data.data.forEach((row) => {
          let modRowObj = {};
          modRowObj.id = row.id;
          modRowObj.name = row.row_name;
          const parsedRow = JSON.parse(row.json);
          parsedRow.metadata.id = row.id;
          modRowObj = { ...modRowObj, ...parsedRow };
          rowsModified.push(modRowObj);
        });
      }
      resolve([...rowsModified]);
    },

    rowDataCreateUpdate() {
      this.beeConfig.hooks.getRows.handler(this.saveRowResolve);
    },

    async manageDeleteRow(resolve, reject, args) {
      this.$emit('showLoader');
      try {
        const { token } = Auth.authenticatedUser();
        await axios.delete(
          `${window.config.apiUrl}/user/row/${args.row.metadata.id}`,
          {
            headers: {
              Authorization: 'Bearer ' + token, //the token is a variable which holds the token
            },
          }
        );
        resolve(true);
        this.$emit('hideLoader');
      } catch (err) {
        reject(err);
        this.$emit('hideLoader');
      }
    },
  },
};
</script>

<style lang="scss">
#bee-plugin-container {
  #bee-plugin-container__bee-plugin-loader {
    display: none;
  }
}
</style>
